// ==========================================================================
// usePrintDocumentV2.utils.js
// Helper functions for the V2 print pipeline ("decoupage").
//
// Most functions here are stateless/pure (no React hooks, no component-state
// closures). A small set of iframe normalizers intentionally mutates the print
// clone DOM (for example table/image normalization) before measurement so print
// geometry stays aligned with the editor.
//
// ARCHITECTURE OVERVIEW
// ---------------------
// The V2 pipeline renders PDF pages that match the editor 1:1 by:
//   1. MEASURE  — capture node positions from the editor layout
//   2. SNAPSHOT — apply editor pagination decisions (break contracts)
//   3. PLAN    — assign each node to a page, splitting straddlers
//   4. RENDER  — absolutely position fragments into fixed-size page containers
//
// Content-aware splitting strategy:
//   • Text (p, h1-h6)  → split at the exact line/character boundary
//   • Lists (ul, ol)   → split at LI boundary; straddle LI gets text-split
//   • Tables / divs    → clip with overflow:hidden (no mid-cell splitting)
//
// Watermark text/SVG injection is not in this file — see `usePrintDocument.utils.js`
// and `usePrintDocumentV2.js` (print callback step 1).
//
// QUICK MODULE MAP (exports by responsibility)
// --------------------------------------------
// 1) Iframe normalization (intentional iframe DOM mutations):
//    - normalizeBrokenImagesInIframe
//    - normalizeHandsontableEmptyRowsInIframe
//    - fixBorderBottomZIndex
//
// 2) Measurement + snapshot overlay:
//    - measureSection
//    - applyEditorPaginationSnapshot
//    - buildPageStructure
//
// 3) Text/list classification and splitting:
//    - classifyLineVisibilityAtCutInIframe
//    - splitTextContentAtHeight
//    - splitAtContractAnchor
//    - splitContainerChildrenAtHeight
//    - splitListAtHeight
//    - splitNodeAtHeight
//    - suppressContinuationMarkerAfterListSplit
//
// 4) Shared utilities:
//    - collectTextNodes / cleanEmptyInlines
//    - normalizeBoundaryText / hasRiskySplitOverlap
//    - createMeasurementClone
//    - waitForExportReadiness family
//    - preserveJustification
//
// See src/pages/report-workspace/V2/pagination/readme/print-v2-pagination-contract.md
// for the full contract spec.
//
// ENTRY-POINT BREADCRUMBS (where these helpers are consumed)
// -----------------------------------------------------------
// If you're debugging behavior in usePrintDocumentV2.js, follow this map:
//
// A) Print callback setup/normalization (Step 1-2)
//    - normalizeBrokenImagesInIframe
//    - normalizeHandsontableEmptyRowsInIframe
//    - waitForExportReadiness
//    - Watermark: usePrintDocumentV2 calls sanitizeWatermarkText / injectWatermark from
//      usePrintDocument.utils.js when withWatermark is true
//
// B) Measurement + snapshot handoff (Step 3)
//    - measureSection
//    - applyEditorPaginationSnapshot
//
// C) Planner split/decision paths (buildDecoupageContainers)
//    - text/list boundary classification + split helpers
//    - classifyLineVisibilityAtCutInIframe
//    - splitAtContractAnchor / splitNodeAtHeight
//    - suppressContinuationMarkerAfterListSplit
//    - preserveJustification
//    - hasRiskySplitOverlap / normalizeBoundaryText
//
// D) Render-time table fixes and layering
//    - fixBorderBottomZIndex
// ==========================================================================

import {
  LANDSCAPE_PAGE_HEIGHT,
  LANDSCAPE_PAGE_WIDTH,
  PAGE_DIVIDER_HEIGHT,
  PORTRAIT_PAGE_HEIGHT,
  PORTRAIT_PAGE_WIDTH,
  normalizeBrokenImageElement,
  buildStableNodeKey,
  getNodePath,
  resolveNodePath,
  pruneAfter,
  pruneBefore,
  forceStyleOverride,
  forceStyleOverrides,
  getElementRect,
  getRectDelta,
  flushDocumentLayout,
  imageHasRenderableSrc,
} from '../pages/report-workspace/V2/pagination';
import {
  LAYOUT_STABLE_FRAMES,
  LAYOUT_DELTA_EPSILON,
  EXPORT_READY_TIMEOUT_MS,
  EXPORT_READY_MIN_STAGE_TIMEOUT_MS,
  PRINT_AUTOFIT_MIN_FONT_SIZE_PX,
  PRINT_AUTOFIT_MAX_ITERATIONS,
  PRINT_AUTOFIT_TOLERANCE_PX,
} from './usePrintDocumentV2.constants';

/**
 * Replay live editor scroll offsets into the print iframe clone.
 *
 * Why this exists:
 * - `react-to-print` clones markup/styles but does not reliably preserve
 *   runtime scroll offsets (`scrollLeft`/`scrollTop`).
 * - Decoupage split decisions and final visual output should reflect what
 *   users were looking at when they triggered print.
 *
 * Notes:
 * - Traversal is index-based over matching subtree shapes.
 * - We intentionally stop at the shared child count to stay resilient to
 *   minor clone-time DOM differences.
 *
 * @param {Element | null} sourceRootEl
 * @param {Document} iframeDoc
 * @returns {{appliedCount:number,comparedCount:number,sourceFound:boolean,iframeFound:boolean}}
 */
export function replayScrollStateFromSourceToIframe(sourceRootEl, iframeDoc) {
  if (!sourceRootEl || !iframeDoc?.body) {
    return {
      appliedCount: 0,
      comparedCount: 0,
      sourceFound: false,
      iframeFound: false,
    };
  }
  const iframeRootEl = iframeDoc.body.querySelector('.tiptap-container');
  if (!iframeRootEl) {
    return {
      appliedCount: 0,
      comparedCount: 0,
      sourceFound: true,
      iframeFound: false,
    };
  }

  const stack = [[sourceRootEl, iframeRootEl]];
  let appliedCount = 0;
  let comparedCount = 0;

  while (stack.length) {
    const [src, dst] = stack.pop();
    if (!src || !dst) continue;
    comparedCount += 1;
    const srcLeft = src.scrollLeft || 0;
    const srcTop = src.scrollTop || 0;
    if (srcLeft || srcTop) {
      dst.scrollLeft = srcLeft;
      dst.scrollTop = srcTop;
      appliedCount += 1;
    }
    const srcChildren = src.children || [];
    const dstChildren = dst.children || [];
    const childCount = Math.min(srcChildren.length, dstChildren.length);
    for (let i = childCount - 1; i >= 0; i -= 1) {
      stack.push([srcChildren[i], dstChildren[i]]);
    }
  }

  return {
    appliedCount,
    comparedCount,
    sourceFound: true,
    iframeFound: true,
  };
}

// ─── Iframe Content Normalizers ──────────────────────────────────────────────
// These run inside the print iframe during print preparation to fix rendering
// discrepancies between the live editor and the iframe clone.
// They are applied before measurement and may be re-applied after CSS
// neutralization to preserve parity if extracted print rules reset styles.
//
// normalizeBrokenImagesInIframe:
//   Images that fail to load (naturalWidth === 0) are resized to 18×18
//   placeholders and centered in their wrapper. ProseMirror separator
//   images are hidden entirely.
//
// normalizeHandsontableEmptyRowsInIframe:
//   Handsontable empty rows may render at different heights in the iframe
//   than in the editor (often due to missing CSS or reflow differences).
//   This measures empty-row heights from the source document and forces
//   the iframe copy to match, preventing cumulative vertical drift.
//   Also corrects table inset offsets when wrapper→table relative position
//   differs between source and iframe.

/**
 * Normalize broken images inside the print iframe so cross-browser
 * rendering matches the editor pagination baseline.
 *
 * Behavior:
 * - hides ProseMirror separator images
 * - forces broken images (naturalWidth === 0) to 18x18
 * - recenters the wrapper chain used by tiptap image resize wrappers
 *
 * @param {Document} iframeDoc
 * @returns {void}
 */
export function normalizeBrokenImagesInIframe(iframeDoc) {
  if (!iframeDoc) return;
  iframeDoc.querySelectorAll('img').forEach((img) => {
    if (img.classList.contains('ProseMirror-separator')) {
      img.style.setProperty('display', 'none', 'important');
      return;
    }
    normalizeBrokenImageElement(img);
  });
}

/**
 * Re-validate template editable-zone font fitting inside the print iframe.
 *
 * Why:
 * - Editable zones can resolve a fitted size in the live editor, but print-time
 *   reflow (fonts, zoom normalization, viewport width differences) can still
 *   cause a one-line overflow in the iframe clone.
 * - This pass re-checks only zones opted into auto-resize and nudges font size
 *   down with a bounded binary search when needed.
 *
 * @param {Document} iframeDoc
 * @param {Window} iframeWindow
 * @returns {void}
 */
export function revalidateEditableZoneAutoResizeInIframe(
  iframeDoc,
  iframeWindow
) {
  if (!iframeDoc || !iframeWindow) return;

  iframeDoc.querySelectorAll('.template-zone').forEach((zoneEl) => {
    const proseMirror = zoneEl.querySelector('.ProseMirror');
    if (!proseMirror) return;

    const zoneRoot = proseMirror.closest('[data-template-autoresize]');
    const isAuto =
      zoneRoot?.getAttribute('data-template-autoresize') === 'true';
    if (!isAuto) return;

    const resolvedSizeAttrPx = Number.parseFloat(
      zoneRoot?.getAttribute('data-template-autoresize-resolved-font-size-px')
    );

    const sizePx =
      Number.isFinite(resolvedSizeAttrPx) && resolvedSizeAttrPx > 0
        ? resolvedSizeAttrPx
        : Number.parseFloat(
            iframeWindow.getComputedStyle(proseMirror).fontSize
          );
    if (!Number.isFinite(sizePx) || sizePx <= 0) return;

    const zoneStyles = iframeWindow.getComputedStyle(zoneEl);
    const paddingTop = Number.parseFloat(zoneStyles.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(zoneStyles.paddingBottom) || 0;
    const availableHeight = zoneEl.clientHeight - paddingTop - paddingBottom;

    const doesFit = (fontSizePx) => {
      proseMirror.style.fontSize = `${fontSizePx}px`;
      return (
        proseMirror.scrollHeight <= availableHeight &&
        proseMirror.scrollWidth <= proseMirror.clientWidth
      );
    };

    if (
      availableHeight <= 0 ||
      (proseMirror.scrollHeight <= availableHeight &&
        proseMirror.scrollWidth <= proseMirror.clientWidth)
    ) {
      return;
    }

    let lower = PRINT_AUTOFIT_MIN_FONT_SIZE_PX;
    let upper = sizePx;
    let fitted = PRINT_AUTOFIT_MIN_FONT_SIZE_PX;

    if (doesFit(upper)) {
      fitted = upper;
    } else {
      for (
        let i = 0;
        i < PRINT_AUTOFIT_MAX_ITERATIONS &&
        upper - lower > PRINT_AUTOFIT_TOLERANCE_PX;
        i += 1
      ) {
        const mid = (lower + upper) / 2;
        if (doesFit(mid)) {
          fitted = mid;
          lower = mid;
        } else {
          upper = mid;
        }
      }
    }

    const normalizedFitted = Number(fitted.toFixed(2));
    proseMirror.style.fontSize = `${normalizedFitted}px`;
  });
}

/**
 * Apply iframe content normalizations used by print pipelines, then flush layout.
 *
 * @param {Document} iframeDoc
 * @param {Window} iframeWindow
 * @param {Document} [sourceDocument=document]
 * @returns {void}
 */
export function normalizeIframeContent(
  iframeDoc,
  iframeWindow,
  sourceDocument = document
) {
  if (!iframeDoc || !iframeWindow) return;
  normalizeBrokenImagesInIframe(iframeDoc);
  revalidateEditableZoneAutoResizeInIframe(iframeDoc, iframeWindow);
  normalizeHandsontableEmptyRowsInIframe(iframeDoc, sourceDocument);
  flushDocumentLayout(iframeDoc);
}

function isEmptyTableRowCells(cells) {
  return cells.every((cell) => {
    const txt = (cell.textContent || '').replace(/\u00a0/g, '').trim();
    return txt.length === 0;
  });
}

function collectEmptyRowHeights(table) {
  if (!table) return [];
  const rows = Array.from(table.querySelectorAll('tr'));
  const heights = [];
  rows.forEach((row) => {
    const cells = Array.from(row.cells || []);
    if (!cells.length) return;
    if (!isEmptyTableRowCells(cells)) return;
    const rowH = row.getBoundingClientRect().height;
    const cellH = cells[0]?.getBoundingClientRect?.().height || 0;
    const h = rowH || cellH || 0;
    if (h > 0) heights.push(h);
  });
  return heights;
}

/**
 * Robust representative value for sampled row heights.
 * Median is used (instead of per-row copy/average) to dampen outliers from
 * sub-pixel rounding and clone-only measurement noise.
 */
function medianNumber(vals) {
  if (!vals.length) return null;
  const arr = vals.slice().sort((a, b) => a - b);
  const mid = Math.floor(arr.length / 2);
  if (arr.length % 2 === 0) return (arr[mid - 1] + arr[mid]) / 2;
  return arr[mid];
}

function getTableMatchKey(table) {
  if (!table) return '';
  const ownerDataId =
    table.closest?.('[data-id]')?.getAttribute?.('data-id') || '';
  const hotRoot = table.closest?.('.handsontable') || table.parentElement;
  const siblings = hotRoot
    ? Array.from(hotRoot.querySelectorAll('table'))
    : [table];
  const localIdx = Math.max(0, siblings.indexOf(table));
  return `${ownerDataId}::${localIdx}`;
}

function getInscopeWrapper(tableNode) {
  return tableNode?.querySelector?.('.inscope-formatted-table') || null;
}

function getNodeTable(wrapper) {
  return wrapper?.querySelector?.('.handsontable.node-table') || null;
}

/**
 * Lock element block geometry to a target height.
 * - exact=true  => force fixed box (height + min-height)
 * - exact=false => keep min-height floor, allow natural growth above target
 */
function lockElementHeight(el, targetHeight, {exact = true} = {}) {
  if (!el || !Number.isFinite(targetHeight)) return;
  forceStyleOverride(el, 'min-height', `${targetHeight}px`);
  if (exact) {
    forceStyleOverride(el, 'height', `${targetHeight}px`);
  } else {
    forceStyleOverride(el, 'height', null);
  }
}

function normalizeEmptyTableCellHeight(cell, targetHeight) {
  if (!cell) return;
  lockElementHeight(cell, targetHeight);
  forceStyleOverrides(cell, {
    'line-height': `${targetHeight}px`,
    'padding-top': '0',
    'padding-bottom': '0',
  });
}

function normalizeEmptyTableRowHeight(row, targetHeight) {
  if (!row) return;
  Array.from(row.cells || []).forEach((cell) =>
    normalizeEmptyTableCellHeight(cell, targetHeight)
  );
  lockElementHeight(row, targetHeight);
}

function getRectScaleW(srcRect, dstRect, fallbackScale = 1) {
  const rawScale =
    Number.isFinite(srcRect?.width) &&
    Number.isFinite(dstRect?.width) &&
    dstRect.width > 0
      ? srcRect.width / dstRect.width
      : fallbackScale;
  return Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
}

function normalizeByScale(value, scale) {
  return Number.isFinite(value) && Number.isFinite(scale) && scale > 0
    ? value / scale
    : value;
}

function getTableNormalizationMetrics({
  srcTableRect,
  dstTableRect,
  srcHotRect,
  dstHotRect,
  srcEmptyHeight,
}) {
  const tblWidthScale = getRectScaleW(srcTableRect, dstTableRect, 1);
  const hotWidthScale = getRectScaleW(srcHotRect, dstHotRect, tblWidthScale);
  return {
    normalizedSrcTableH: normalizeByScale(srcTableRect?.height, tblWidthScale),
    normalizedSrcHotH: normalizeByScale(srcHotRect?.height, hotWidthScale),
    normalizedSrcEmptyH: normalizeByScale(srcEmptyHeight, tblWidthScale),
  };
}

/**
 * Align Handsontable row geometry in the print iframe to source editor geometry.
 *
 * This prevents pagination drift caused by iframe-only row-height differences.
 * It also compensates top-inset mismatch between table wrapper and node-table.
 * Source geometry is normalized into iframe-space using per-table width scale
 * so browser/in-app zoom compensation does not inflate print-time table sizing.
 *
 * Intent note:
 * - keep downstream sibling placement stable by locking outer table-wrapper geometry
 * - avoid unnecessary hard clipping by letting inner table content paint overflow
 *   when it exceeds the locked baseline slightly.
 *
 * @param {Document} iframeDoc
 * @param {Document} [sourceDocument=document]
 * @returns {void}
 */
export function normalizeHandsontableEmptyRowsInIframe(
  iframeDoc,
  sourceDocument = document
) {
  if (!iframeDoc) return;
  const srcTables = Array.from(
    sourceDocument.querySelectorAll('.document-section .handsontable table')
  );
  const srcByKey = new Map(
    srcTables.map((t, idx) => [getTableMatchKey(t) || `idx::${idx}`, t])
  );
  const dstTables = Array.from(
    iframeDoc.querySelectorAll('.handsontable table')
  );

  dstTables.forEach((table, tableIdx) => {
    const key = getTableMatchKey(table);
    const srcTable = srcByKey.get(key) || srcTables[tableIdx] || null;
    const srcEmptyHeight = medianNumber(collectEmptyRowHeights(srcTable));
    if (!Number.isFinite(srcEmptyHeight) || srcEmptyHeight <= 0) return;

    // Keep table wrapper/table block heights in lockstep with source.
    // Windows DPR/fractional metrics can leave total table height slightly
    // different even when row heights are normalized, which shifts following
    // content in print composition.
    //
    // Intentional asymmetry:
    // - inner table: min-height only (visual overflow can remain visible)
    // - outer .handsontable: exact height lock (downstream sibling geometry stable)
    const srcTableRect = getElementRect(srcTable);
    const dstTableRect = getElementRect(table);
    const srcHotRoot = srcTable?.closest?.('.handsontable');
    const dstHotRoot = table?.closest?.('.handsontable');
    const srcHotRect = getElementRect(srcHotRoot);
    const dstHotRect = getElementRect(dstHotRoot);
    const {normalizedSrcTableH, normalizedSrcHotH, normalizedSrcEmptyH} =
      getTableNormalizationMetrics({
        srcTableRect,
        dstTableRect,
        srcHotRect,
        dstHotRect,
        srcEmptyHeight,
      });
    const rows = Array.from(table.querySelectorAll('tr'));
    rows.forEach((row) => {
      const cells = Array.from(row.cells || []);
      if (!cells.length) return;
      if (!isEmptyTableRowCells(cells)) return;
      normalizeEmptyTableRowHeight(row, normalizedSrcEmptyH);
    });
    const tableHeightDelta = getRectDelta(srcTableRect, dstTableRect, 'height');
    const hotHeightDelta = getRectDelta(srcHotRect, dstHotRect, 'height');
    if (
      tableHeightDelta > LAYOUT_DELTA_EPSILON &&
      Number.isFinite(normalizedSrcTableH)
    ) {
      // Keep table content free to grow if needed (avoid forced shrink clipping),
      // while wrapper lock below stabilizes downstream geometry.
      lockElementHeight(table, normalizedSrcTableH, {exact: false});
      forceStyleOverride(table, 'overflow', 'visible');
    }
    if (
      hotHeightDelta > LAYOUT_DELTA_EPSILON &&
      Number.isFinite(normalizedSrcHotH)
    ) {
      // Keep outer block geometry stable for downstream sibling positioning.
      lockElementHeight(dstHotRoot, normalizedSrcHotH);
      // Allow minor inner paint overflow to remain visible instead of clipping.
      forceStyleOverride(dstHotRoot, 'overflow', 'visible');
    }
  });

  const srcTableNodes = Array.from(
    sourceDocument.querySelectorAll(
      '.document-section [data-type="table"][data-id]'
    )
  );
  const srcById = new Map(
    srcTableNodes.map((n) => [n.getAttribute('data-id') || '', n])
  );
  const dstTableNodes = Array.from(
    iframeDoc.querySelectorAll('.document-section [data-type="table"][data-id]')
  );
  dstTableNodes.forEach((dstNode) => {
    const tableId = dstNode.getAttribute('data-id') || '';
    if (!tableId) return;
    const srcNode = srcById.get(tableId);
    if (!srcNode) return;

    const srcWrapper = getInscopeWrapper(srcNode);
    const dstWrapper = getInscopeWrapper(dstNode);
    if (!srcWrapper || !dstWrapper) return;

    const srcNodeTable = getNodeTable(srcWrapper);
    const dstNodeTable = getNodeTable(dstWrapper);
    if (!srcNodeTable || !dstNodeTable) return;

    const srcWrapperRect = srcWrapper.getBoundingClientRect();
    const dstWrapperRect = dstWrapper.getBoundingClientRect();
    const srcTableRect = srcNodeTable.getBoundingClientRect();
    const dstTableRect = dstNodeTable.getBoundingClientRect();

    const srcInsetTop = srcTableRect.top - srcWrapperRect.top;
    const dstInsetTop = dstTableRect.top - dstWrapperRect.top;
    const insetScale = getRectScaleW(srcTableRect, dstTableRect, 1);
    const normalizedSrcInsetTop = normalizeByScale(srcInsetTop, insetScale);
    const insetDelta = normalizedSrcInsetTop - dstInsetTop;
    if (
      !Number.isFinite(insetDelta) ||
      Math.abs(insetDelta) < LAYOUT_DELTA_EPSILON
    )
      return;

    forceStyleOverrides(dstNodeTable, {
      position: 'relative',
      top: `${insetDelta}px`,
    });
  });
}

// ─── Measurement + Snapshot Mapping ──────────────────────────────────────────

/**
 * Measure a `.document-section` element in the print iframe.
 *
 * Walks the section's page-break DOM to build page structure, then iterates
 * every direct child of the `.tiptap` editor root to capture per-node
 * bounding geometry (pageIdx, withinPageTop/Bottom, left, width, height).
 *
 * Zoom-compensation: the section may be scaled by browser zoom.  A scale
 * factor is derived from (sectionRect.width / expectedCSSWidth) and all
 * measurements are divided by that factor so output is in unzoomed CSS px.
 *
 * @param {Element} sec  - a `.document-section` element
 * @param {Window}  iWin - the iframe's contentWindow (for getComputedStyle)
 * @returns {object|null} pre-measured data:
 *   - nodes: Array<{ el, dataId, sourceNodeKey, pageIdx, withinPageTop,
 *       withinPageBottom, left, width, height }>
 *   - tiptapClassName, tiptapInheritedCSS: styling context for measurement clones
 *   - pageStructure: Array<{ pageIdx, pageTopY, headerHeight, footerHeight,
 *       headerBottomY, pageBottomY, footerTopY, headerEl?, footerEl? }>
 *   - isLandscape, pageWidth, pageHeight, measurementScale
 */
export function measureSection(sec, iWin) {
  if (!sec || !iWin) return null;
  const PAGE_ASSIGN_EPSILON_PX = 1;
  const isLandscape = sec.classList.contains('landscape');
  const pageWidth = isLandscape ? LANDSCAPE_PAGE_WIDTH : PORTRAIT_PAGE_WIDTH;
  const pageHeight = isLandscape ? LANDSCAPE_PAGE_HEIGHT : PORTRAIT_PAGE_HEIGHT;

  const pageRef = sec.children[0];
  const tiptap = sec.querySelector('.tiptap');
  if (!pageRef || !tiptap || !tiptap.children.length) return null;

  const pageStructure = buildPageStructure(pageRef);
  const numPages = pageStructure.length;
  if (numPages === 0) return null;

  const secRect = sec.getBoundingClientRect();
  const tiptapCs = iWin.getComputedStyle(tiptap);
  const secCs = iWin.getComputedStyle(sec);
  const sectionHeaderHeight =
    parseFloat(secCs.getPropertyValue('--page-header-height')) || 46;
  const sectionFooterHeight =
    parseFloat(secCs.getPropertyValue('--page-footer-height')) || 46;

  const tiptapClassName = tiptap.className;
  const tiptapInheritedCSS = [
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'line-height',
    'letter-spacing',
    'word-spacing',
    'text-rendering',
    'font-kerning',
    'font-variant-ligatures',
    'font-synthesis',
    '-webkit-font-smoothing',
    'color',
    'text-align',
  ]
    .map((p) => `${p}:${tiptapCs.getPropertyValue(p)}`)
    .join(';');

  const expectedCSSHeight =
    numPages * pageHeight + (numPages - 1) * PAGE_DIVIDER_HEIGHT;
  const widthScale = secRect.width / pageWidth;
  const heightScale = secRect.height / expectedCSSHeight;
  const zoom =
    Number.isFinite(widthScale) && widthScale > 0
      ? widthScale
      : Number.isFinite(heightScale) && heightScale > 0
        ? heightScale
        : 1;

  for (let pi = 0; pi < pageStructure.length; pi++) {
    const ps = pageStructure[pi];
    if (ps.headerEl) {
      const hRect = ps.headerEl.getBoundingClientRect();
      ps.pageTopY = (hRect.top - secRect.top) / zoom;
      ps.headerHeight = hRect.height / zoom;
    } else {
      ps.pageTopY = pi * (pageHeight + PAGE_DIVIDER_HEIGHT);
      ps.headerHeight = sectionHeaderHeight;
    }
    if (ps.footerEl) {
      ps.footerHeight = ps.footerEl.getBoundingClientRect().height / zoom;
    } else {
      ps.footerHeight = sectionFooterHeight;
    }
    ps.headerBottomY = ps.pageTopY + ps.headerHeight;
    ps.pageBottomY = ps.pageTopY + pageHeight;
    ps.footerTopY = ps.pageBottomY - ps.footerHeight;
  }

  const nodes = [];
  for (let i = 0; i < tiptap.children.length; i++) {
    const el = tiptap.children[i];
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;

    const relTop = (r.top - secRect.top) / zoom;
    let pageIdx = 0;
    for (let pi = pageStructure.length - 1; pi >= 0; pi--) {
      // Browser/zoom rounding can put boundary-adjacent nodes a fraction of a
      // pixel above their intended page start; tolerate a tiny epsilon so page
      // bucketing remains stable across zoom levels/browsers.
      if (relTop >= pageStructure[pi].pageTopY - PAGE_ASSIGN_EPSILON_PX) {
        pageIdx = pi;
        break;
      }
    }

    const withinPageTop = relTop - pageStructure[pageIdx].pageTopY;
    const nodeHeight = r.height / zoom;
    const withinPageBottom = withinPageTop + nodeHeight;

    const _nDataId = el.getAttribute('data-id') || null;
    const _nLeft = (r.left - secRect.left) / zoom;
    const _nWidth = r.width / zoom;
    nodes.push({
      el,
      dataId: _nDataId,
      sourceNodeKey: buildStableNodeKey(_nDataId, el.tagName, i),
      pageIdx,
      withinPageTop,
      withinPageBottom,
      left: _nLeft,
      width: _nWidth,
      height: nodeHeight,
    });
  }

  return {
    nodes,
    tiptapClassName,
    tiptapInheritedCSS,
    pageStructure,
    isLandscape,
    pageWidth,
    pageHeight,
    measurementScale: zoom,
  };
}

/**
 * Overlay editor pagination snapshot data onto iframe-measured node geometry.
 *
 * This is the bridge between the editor's pagination decisions (producer) and
 * the print pipeline's planner (consumer).  It walks every measured node,
 * matches it to the corresponding snapshot entry by `sourceNodeKey` or
 * `dataId`, then selectively overwrites:
 *
 *   - **Page structure** (portrait only): pageTopY, header/footer heights.
 *   - **Node geometry** (portrait only): withinPageTop, withinPageBottom.
 *   - **Break contracts** (all orientations): decision + splitAnchor.
 *   - **Line visibility** (all orientations): editor-side above/below flags.
 *
 * A secondary pass aligns wrapper nodes (no `data-id`) by computing the
 * offset between each wrapper and its closest mapped descendant, preserving
 * that spatial relationship after the descendant has been repositioned.
 *
 * Snapshot values are divided by `snapshotScale` (median page-height ratio)
 * to normalise from editor-zoom space back to unzoomed CSS px.
 *
 * Also populates `preMeasured.authoritativeNodeByRef` — a Map used by the
 * planner for invariant checks and contract-decision lookups.
 *
 * @param {object} preMeasured      - output of measureSection()
 * @param {object} sectionSnapshot  - one section entry from buildPaginationSnapshot()
 * @param {object} [options]
 * @param {boolean} [options.applyGeometry=true]           - overlay vertical position
 * @param {boolean} [options.applyPageAssignment=false]    - override pageIdx from snapshot
 * @param {boolean} [options.applyHorizontalGeometry=false] - overlay left/width
 * @param {boolean} [options.applyWrapperAlignment]        - realign wrapper nodes (defaults to applyGeometry)
 * @returns {object} the mutated preMeasured object
 */
export function applyEditorPaginationSnapshot(
  preMeasured,
  sectionSnapshot,
  options = {}
) {
  if (
    !preMeasured ||
    !sectionSnapshot ||
    !Array.isArray(sectionSnapshot.nodes)
  ) {
    return preMeasured;
  }

  const pageCount = preMeasured.pageStructure?.length || 0;
  if (!pageCount) return preMeasured;
  const pageHeight = preMeasured.pageHeight || PORTRAIT_PAGE_HEIGHT;

  const applyGeometry = options.applyGeometry !== false;
  const applyPageAssignment = options.applyPageAssignment === true;
  const applyHorizontalGeometry = options.applyHorizontalGeometry === true;
  const applyWrapperAlignment =
    options.applyWrapperAlignment !== undefined
      ? options.applyWrapperAlignment
      : applyGeometry;
  const collectScales = [];
  if (Array.isArray(sectionSnapshot?.pageStructure)) {
    sectionSnapshot.pageStructure.forEach((ps) => {
      const top = Number(ps?.pageTopY);
      const bottom = Number(ps?.pageBottomY);
      const h = bottom - top;
      if (Number.isFinite(h) && h > 0) {
        collectScales.push(h / pageHeight);
      }
    });
  }
  const snapshotScale = (() => {
    const vals = collectScales.filter((v) => Number.isFinite(v) && v > 0);
    if (!vals.length) return 1;
    const sorted = vals.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  })();
  const normalizeFromSnapshot = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n / snapshotScale;
  };

  if (
    applyGeometry &&
    !preMeasured.isLandscape &&
    Array.isArray(sectionSnapshot.pageStructure) &&
    sectionSnapshot.pageStructure.length === preMeasured.pageStructure.length
  ) {
    for (let p = 0; p < preMeasured.pageStructure.length; p++) {
      const ps = preMeasured.pageStructure[p];
      const editorPs = sectionSnapshot.pageStructure[p];
      const pageTopY = normalizeFromSnapshot(editorPs?.pageTopY);
      const pageBottomY = normalizeFromSnapshot(editorPs?.pageBottomY);
      const headerHeight = normalizeFromSnapshot(editorPs?.headerHeight);
      const footerHeight = normalizeFromSnapshot(editorPs?.footerHeight);
      const headerBottomY = normalizeFromSnapshot(editorPs?.headerBottomY);
      const footerTopY = normalizeFromSnapshot(editorPs?.footerTopY);

      if (Number.isFinite(pageTopY)) ps.pageTopY = pageTopY;
      if (Number.isFinite(pageBottomY)) ps.pageBottomY = pageBottomY;
      if (Number.isFinite(headerHeight) && headerHeight > 0) {
        ps.headerHeight = headerHeight;
      }
      if (Number.isFinite(footerHeight) && footerHeight > 0) {
        ps.footerHeight = footerHeight;
      }

      ps.pageBottomY = ps.pageTopY + pageHeight;
      ps.headerBottomY = Number.isFinite(headerBottomY)
        ? headerBottomY
        : ps.pageTopY + ps.headerHeight;
      ps.footerTopY = Number.isFinite(footerTopY)
        ? footerTopY
        : ps.pageBottomY - ps.footerHeight;
      if (!Number.isFinite(ps.headerBottomY)) {
        ps.headerBottomY = ps.pageTopY + ps.headerHeight;
      }
      if (!Number.isFinite(ps.footerTopY)) {
        ps.footerTopY = ps.pageBottomY - ps.footerHeight;
      }
    }
  }

  const editorNodeByKey = new Map();
  const editorNodesByIdQueue = new Map();
  const authoritativeNodeByRef = new Map();
  const breakContractByKey = new Map();
  if (
    sectionSnapshot?.breakContract &&
    typeof sectionSnapshot.breakContract === 'object'
  ) {
    Object.values(sectionSnapshot.breakContract).forEach((entry) => {
      if (!entry?.sourceNodeKey) return;
      breakContractByKey.set(entry.sourceNodeKey, entry);
    });
  }
  sectionSnapshot.nodes.forEach((node, idx) => {
    if (!node) return;
    if (!Number.isInteger(node.pageIdx)) return;
    const sourceNodeKey =
      node.sourceNodeKey || buildStableNodeKey(node.dataId, node.tag, idx);
    editorNodeByKey.set(sourceNodeKey, node);
    if (node.dataId) {
      if (!editorNodesByIdQueue.has(node.dataId)) {
        editorNodesByIdQueue.set(node.dataId, []);
      }
      editorNodesByIdQueue.get(node.dataId).push(node);
    }
    authoritativeNodeByRef.set(sourceNodeKey, {
      pageIdx: node.pageIdx,
      decision: node.decision || null,
      withinPageTop: Number.isFinite(node.withinPageTop)
        ? node.withinPageTop
        : null,
      tag: (node.tag || '').toLowerCase(),
    });
  });

  if (!editorNodeByKey.size && !editorNodesByIdQueue.size) return preMeasured;
  preMeasured.authoritativeNodeByRef = authoritativeNodeByRef;
  const originalWithinPageTopByNode = new Map();
  preMeasured.nodes.forEach((n) => {
    originalWithinPageTopByNode.set(n, n.withinPageTop);
  });

  for (let i = 0; i < preMeasured.nodes.length; i++) {
    const node = preMeasured.nodes[i];
    const dataId = node.dataId || node.el?.getAttribute('data-id');
    const sourceNodeKey =
      node.sourceNodeKey || buildStableNodeKey(dataId, node.el?.tagName, i);
    let editorNode =
      (sourceNodeKey && editorNodeByKey.get(sourceNodeKey)) || null;
    if (!editorNode && dataId) {
      const q = editorNodesByIdQueue.get(dataId);
      if (q && q.length) editorNode = q.shift();
    }
    if (!editorNode) continue;

    // Keep snapshot page assignment narrowly scoped to wrapper nodes; using it
    // for all nodes can reintroduce zoom-sensitive routing drift.
    const shouldApplyPageAssignment = applyPageAssignment && !node.dataId;
    if (applyGeometry || shouldApplyPageAssignment) {
      const originalPageIdx = node.pageIdx;
      const targetPageIdx = Math.max(
        0,
        Math.min(pageCount - 1, editorNode.pageIdx)
      );
      node.pageIdx = targetPageIdx;

      if (applyGeometry) {
        const normalizedWithinPageTop = normalizeFromSnapshot(
          editorNode.withinPageTop
        );
        if (Number.isFinite(normalizedWithinPageTop)) {
          node.withinPageTop = normalizedWithinPageTop;
        } else {
          const oldPageTopY =
            preMeasured.pageStructure[originalPageIdx]?.pageTopY || 0;
          const relTop = oldPageTopY + node.withinPageTop;
          const targetPageTopY =
            preMeasured.pageStructure[targetPageIdx]?.pageTopY || 0;
          node.withinPageTop = relTop - targetPageTopY;
        }
      } else {
        // Wrapper page-assignment mode: prefer snapshot withinPageTop when
        // available, otherwise fall back to relative transfer.
        const normalizedWithinPageTop = normalizeFromSnapshot(
          editorNode.withinPageTop
        );
        if (Number.isFinite(normalizedWithinPageTop)) {
          node.withinPageTop = normalizedWithinPageTop;
        } else {
          const oldPageTopY =
            preMeasured.pageStructure[originalPageIdx]?.pageTopY || 0;
          const relTop = oldPageTopY + node.withinPageTop;
          const targetPageTopY =
            preMeasured.pageStructure[targetPageIdx]?.pageTopY || 0;
          node.withinPageTop = relTop - targetPageTopY;
        }
      }
    }
    if (applyHorizontalGeometry) {
      const normalizedLeft = normalizeFromSnapshot(editorNode.left);
      const normalizedWidth = normalizeFromSnapshot(editorNode.width);
      if (Number.isFinite(normalizedLeft)) node.left = normalizedLeft;
      if (Number.isFinite(normalizedWidth) && normalizedWidth > 0) {
        node.width = normalizedWidth;
      }
    }
    if (
      editorNode.visibility &&
      typeof editorNode.visibility.above === 'boolean' &&
      typeof editorNode.visibility.below === 'boolean'
    ) {
      node.editorLineVisibility = {
        above: editorNode.visibility.above,
        below: editorNode.visibility.below,
      };
    }
    const contract = breakContractByKey.get(sourceNodeKey);
    if (contract) node.breakContract = contract;
    node.withinPageBottom = node.withinPageTop + node.height;
  }

  if (applyWrapperAlignment) {
    for (let i = 0; i < preMeasured.nodes.length; i++) {
      const wrapperNode = preMeasured.nodes[i];
      if (wrapperNode.dataId) continue;
      if (!wrapperNode.el || !wrapperNode.el.querySelector) continue;

      let bestChild = null;
      let bestDelta = Number.POSITIVE_INFINITY;
      for (let j = 0; j < preMeasured.nodes.length; j++) {
        if (i === j) continue;
        const childNode = preMeasured.nodes[j];
        if (!childNode?.dataId || !childNode?.el) continue;
        if (!wrapperNode.el.contains(childNode.el)) continue;
        const origWrapperTop = originalWithinPageTopByNode.get(wrapperNode);
        const origChildTop = originalWithinPageTopByNode.get(childNode);
        if (!Number.isFinite(origWrapperTop) || !Number.isFinite(origChildTop))
          continue;
        const d = origChildTop - origWrapperTop;
        if (d >= 0 && d < bestDelta) {
          bestDelta = d;
          bestChild = childNode;
        }
      }
      if (!bestChild) continue;

      const origWrapperTop = originalWithinPageTopByNode.get(wrapperNode);
      const origChildTop = originalWithinPageTopByNode.get(bestChild);
      if (!Number.isFinite(origWrapperTop) || !Number.isFinite(origChildTop))
        continue;
      if (!Number.isFinite(bestChild.withinPageTop)) continue;

      const originalRelOffset = origChildTop - origWrapperTop;
      const correctedWrapperTop = bestChild.withinPageTop - originalRelOffset;
      if (!Number.isFinite(correctedWrapperTop)) continue;
      if (
        Math.abs(correctedWrapperTop - wrapperNode.withinPageTop) <
        LAYOUT_DELTA_EPSILON
      )
        continue;

      wrapperNode.withinPageTop = correctedWrapperTop;
      wrapperNode.withinPageBottom =
        wrapperNode.withinPageTop + wrapperNode.height;
    }
  }

  return preMeasured;
}

// ─── DOM Tree Manipulation ───────────────────────────────────────────────────

/**
 * Recursively collect every text node under `root`.
 * Unlike the version in usePagination.utils.js, this collects ALL text nodes
 * (including whitespace-only) because the binary-search split algorithm needs
 * to see every node to compute accurate character offsets.
 *
 * @param {Node} node  - DOM node to walk
 * @param {Text[]} out - accumulator (default [])
 * @returns {Text[]} all descendant text nodes
 */
export function collectTextNodes(node, out = []) {
  if (node.nodeType === Node.TEXT_NODE) out.push(node);
  else for (const c of node.childNodes) collectTextNodes(c, out);
  return out;
}

/**
 * Remove empty inline elements left behind after pruning.
 * Preserves void elements (br, img, hr) that are valid even when empty.
 */
export function cleanEmptyInlines(node) {
  for (let i = node.childNodes.length - 1; i >= 0; i--) {
    const c = node.childNodes[i];
    if (c.nodeType === Node.ELEMENT_NODE) {
      cleanEmptyInlines(c);
      if (
        c.childNodes.length === 0 &&
        !['br', 'img', 'hr'].includes(c.tagName.toLowerCase())
      ) {
        node.removeChild(c);
      }
    } else if (c.nodeType === Node.TEXT_NODE && c.textContent === '') {
      node.removeChild(c);
    }
  }
}

// ─── Text Comparison ─────────────────────────────────────────────────────────

/**
 * Collapse whitespace runs and trim — used to compare split fragments
 * against the original text to detect degenerate/duplicate splits.
 */
export function normalizeBoundaryText(text) {
  return (text || '').replace(/\s+/g, ' ').trim();
}

/**
 * Detect whether a proposed split would produce degenerate fragments that
 * overlap or nearly duplicate the original.  Returns true if the split
 * should be rejected.
 *
 * Guards against:
 *  - Full duplication (top or bottom equals original)
 *  - Near-full clones (>98% of original length)
 *  - Non-prefix/suffix relationships (fragments don't compose the original)
 *
 * @param {string} originalText - normalized text of the unsplit element
 * @param {string} topText      - normalized text of the top fragment
 * @param {string} bottomText   - normalized text of the bottom fragment
 * @returns {boolean} true if the split is risky and should be rejected
 */
export function hasRiskySplitOverlap(originalText, topText, bottomText) {
  if (!originalText || !topText || !bottomText) return true;
  if (topText === originalText || bottomText === originalText) return true;

  const oLen = originalText.length;
  const tLen = topText.length;
  const bLen = bottomText.length;
  if (tLen / oLen > 0.98 || bLen / oLen > 0.98) return true;

  const topIsPrefix = originalText.startsWith(topText);
  const bottomIsSuffix = originalText.endsWith(bottomText);
  if (!topIsPrefix || !bottomIsSuffix) return true;

  return false;
}

// ─── Offscreen Measurement Wrapper (DRY) ─────────────────────────────────────

/**
 * Create an offscreen measurement clone of `el` at the specified `width`,
 * mounted in the iframe document.  Returns the clone and a cleanup function
 * that MUST be called when measurement is complete.
 *
 * Every splitting / visibility-classification function needs to measure how
 * an element lays out at a given width.  This helper consolidates the
 * boilerplate: create a hidden wrapper, clone the element, force layout,
 * and return the clone ready for measurement.
 *
 * @param {Element}  el             - element to measure
 * @param {number}   width          - desired CSS-pixel width
 * @param {Document} iframeDoc      - the iframe's document
 * @param {string}   tiptapClassName - className to inherit tiptap styling
 * @param {string}   tiptapCSS      - inline CSS for inherited font properties
 * @returns {{ mClone: Element, cleanup: () => void }}
 */
export function createMeasurementClone(
  el,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  if (!el || !iframeDoc?.body) {
    return {mClone: null, cleanup: () => {}};
  }
  const wrapper = iframeDoc.createElement('div');
  wrapper.className = tiptapClassName;
  wrapper.style.cssText =
    tiptapCSS +
    ';position:absolute;left:-9999px;top:0;width:' +
    width +
    'px;visibility:hidden;pointer-events:none;overflow:visible;';

  const mClone = el.cloneNode(true);
  mClone.style.margin = '0';
  mClone.style.position = 'static';
  mClone.style.width = width + 'px';
  mClone.style.boxSizing = 'border-box';
  wrapper.appendChild(mClone);
  iframeDoc.body.appendChild(wrapper);
  mClone.getBoundingClientRect(); // force layout

  return {
    mClone,
    cleanup: () => iframeDoc.body.removeChild(wrapper),
  };
}

// ─── Export Readiness ────────────────────────────────────────────────────────

/** @returns {Promise<void>} resolves on next animation frame */
export function waitForFrame(iWin) {
  return new Promise((resolve) => iWin.requestAnimationFrame(() => resolve()));
}

/**
 * Race `promise` against a timeout.  If the timeout fires first, resolve
 * with `timeoutValue` (default false) instead of rejecting.
 */
export function withTimeout(promise, timeoutMs, timeoutValue = false) {
  return Promise.race([
    promise,
    new Promise((resolve) =>
      setTimeout(() => resolve(timeoutValue), timeoutMs)
    ),
  ]);
}

/**
 * Capture a lightweight fingerprint of the layout state: section count,
 * sample node count, and aggregated positional checksums.  Used by
 * `waitForStableLayout` to detect when layout has converged.
 */
export function captureLayoutSignature(root) {
  const sectionNodes = Array.from(root.querySelectorAll('.document-section'));
  const sampleNodes = Array.from(root.querySelectorAll('.tiptap > *')).slice(
    0,
    120
  );
  const sectionRect = root.getBoundingClientRect();

  let sumTop = 0;
  let sumBottom = 0;
  for (const n of sampleNodes) {
    const r = n.getBoundingClientRect();
    sumTop += r.top;
    sumBottom += r.bottom;
  }

  return {
    sectionCount: sectionNodes.length,
    sampleCount: sampleNodes.length,
    rootH: Math.round(sectionRect.height * 100) / 100,
    rootW: Math.round(sectionRect.width * 100) / 100,
    sumTop: Math.round(sumTop * 100) / 100,
    sumBottom: Math.round(sumBottom * 100) / 100,
  };
}

/** Compare two layout signatures within `epsilon` tolerance. */
export function signaturesMatch(a, b, epsilon = LAYOUT_DELTA_EPSILON) {
  return (
    a.sectionCount === b.sectionCount &&
    a.sampleCount === b.sampleCount &&
    Math.abs(a.rootH - b.rootH) <= epsilon &&
    Math.abs(a.rootW - b.rootW) <= epsilon &&
    Math.abs(a.sumTop - b.sumTop) <= epsilon &&
    Math.abs(a.sumBottom - b.sumBottom) <= epsilon
  );
}

/** Wait for all web fonts to finish loading. */
export async function waitForFontsReady(iframeDoc, timeoutMs) {
  if (!iframeDoc.fonts?.ready) return true;
  return withTimeout(
    iframeDoc.fonts.ready.then(() => true).catch(() => false),
    timeoutMs,
    false
  );
}

/** Wait for all <img> elements to finish loading (or error). */
export async function waitForImagesReady(root, timeoutMs) {
  const images = Array.from(root.querySelectorAll('img'));
  if (!images.length) return true;

  // Printing from an offscreen iframe can leave lazy images unresolved.
  // Force eager fetch/decoding semantics before readiness checks.
  images.forEach((img) => {
    if (img.classList.contains('ProseMirror-separator')) return;
    try {
      img.loading = 'eager';
      img.decoding = 'sync';
      if (!img.getAttribute('fetchpriority')) {
        img.setAttribute('fetchpriority', 'high');
      }
    } catch (_error) {
      // Best effort only; unsupported attributes should not fail export.
    }
  });

  const pending = images.filter((img) => !img.complete);
  const loadWatchers = pending.map(
    (img) =>
      new Promise((resolve) => {
        const finish = () => resolve(true);
        img.addEventListener('load', finish, {once: true});
        img.addEventListener('error', finish, {once: true});
      })
  );

  const decodeWatchers = images
    .filter(
      (img) =>
        img.complete && img.naturalWidth > 0 && typeof img.decode === 'function'
    )
    .map((img) => img.decode().catch(() => true));

  return withTimeout(
    Promise.all([...loadWatchers, ...decodeWatchers]).then(() => true),
    timeoutMs,
    false
  );
}

/**
 * Wait for post-decoupage image readiness, with an optional minimum settle delay.
 *
 * The settle delay is applied only when the final render tree contains at least
 * one renderable image source (excluding ProseMirror separator images).
 *
 * @param {object} options
 * @param {Element} options.root
 * @param {number} options.timeoutMs
 * @param {number} options.settleDelayMs
 * @returns {Promise<boolean>}
 */
export async function waitForPostDecoupageImagesReady({
  root,
  timeoutMs,
  settleDelayMs,
}) {
  if (!root) return false;

  const hasRenderableImages = Array.from(root.querySelectorAll('img')).some(
    (img) => {
      if (img.classList.contains('ProseMirror-separator')) return false;
      return imageHasRenderableSrc(img);
    }
  );

  if (!hasRenderableImages) {
    return waitForImagesReady(root, timeoutMs);
  }

  const [imagesReady] = await Promise.all([
    waitForImagesReady(root, timeoutMs),
    new Promise((resolve) => setTimeout(resolve, settleDelayMs)),
  ]);
  return imagesReady;
}

/**
 * Poll until the layout signature is stable for `stableFrames` consecutive
 * animation frames.  Returns { ok, signature, stableFrames }.
 */
export async function waitForStableLayout(
  root,
  iWin,
  timeoutMs,
  stableFrames = LAYOUT_STABLE_FRAMES
) {
  const startedAt = Date.now();
  let stableCount = 0;
  let prev = captureLayoutSignature(root);

  while (Date.now() - startedAt < timeoutMs) {
    await waitForFrame(iWin);
    const next = captureLayoutSignature(root);
    if (signaturesMatch(prev, next)) {
      stableCount += 1;
      if (stableCount >= stableFrames) {
        return {ok: true, signature: next, stableFrames};
      }
    } else {
      stableCount = 0;
    }
    prev = next;
  }

  return {ok: false, signature: prev, stableFrames};
}

/**
 * Orchestrate all pre-export readiness checks:
 *   1. Fonts loaded
 *   2. Images loaded
 *   3. Layout stable
 *
 * Each stage gets ~1/3 of the total timeout budget.
 *
 * @returns {{ ok: boolean, reason: string, elapsedMs?: number }}
 */
export async function waitForExportReadiness({
  iframeDoc,
  iWin,
  root,
  timeoutMs = EXPORT_READY_TIMEOUT_MS,
} = {}) {
  if (!iframeDoc || !iWin || !root) {
    return {ok: false, reason: 'missing-arguments'};
  }
  const startedAt = Date.now();
  const perStageTimeout = Math.max(
    EXPORT_READY_MIN_STAGE_TIMEOUT_MS,
    Math.floor(timeoutMs / 3)
  );

  const fontsReady = await waitForFontsReady(iframeDoc, perStageTimeout);
  if (!fontsReady) return {ok: false, reason: 'fonts-timeout'};

  const imagesReady = await waitForImagesReady(root, perStageTimeout);
  if (!imagesReady) return {ok: false, reason: 'images-timeout'};

  const layout = await waitForStableLayout(root, iWin, perStageTimeout);
  if (!layout.ok) return {ok: false, reason: 'layout-unstable'};

  return {
    ok: true,
    reason: 'ready',
    elapsedMs: Date.now() - startedAt,
    signature: layout.signature,
    stableFrames: layout.stableFrames,
  };
}

// ─── Page Structure ──────────────────────────────────────────────────────────

/**
 * Walk the page-break DOM inside a section to build a header/footer map.
 *
 * The editor lays out page breaks as pairs of sentinel elements:
 *   - `.page-break-bottom` = the header of the NEXT page
 *   - `.page-break-top`    = the footer of the CURRENT page
 *
 * Returns an array (indexed by page number) of `{ headerEl, footerEl }`.
 *
 * @param {Element} pageRef - the first child of a `.document-section`
 * @returns {Array<{ headerEl: Element|null, footerEl: Element|null }>}
 */
export function buildPageStructure(pageRef) {
  const children = Array.from(pageRef.children);
  const pages = [];
  let pendingHeader = null;

  for (const child of children) {
    if (!child.classList.contains('page-break')) continue;

    const pbTop = child.querySelector('.page-break-top');
    const pbBottom = child.querySelector('.page-break-bottom');

    if (!pbTop && pbBottom) {
      pendingHeader = pbBottom;
    } else if (pbTop) {
      pages.push({headerEl: pendingHeader, footerEl: pbTop});
      pendingHeader = pbBottom || null;
    }
  }

  return pages;
}

// ─── Iframe Table Fix ────────────────────────────────────────────────────────

/**
 * Fix z-index stacking for Handsontable cells with border-bottom.
 *
 * When a cell has a visible bottom border and the cell below it has a higher
 * z-index, the bottom border gets hidden.  This pass promotes the z-index
 * of border-bottom cells so their borders render on top.
 *
 * Duplicated from usePrintDocument.js — kept separate so V2 stays untouched.
 *
 * @param {Document} iframeDoc
 */
export function fixBorderBottomZIndex(iframeDoc) {
  if (!iframeDoc) return;
  iframeDoc.querySelectorAll('.handsontable table').forEach((table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    const grid = [];
    rows.forEach((tr, rowIdx) => {
      if (!grid[rowIdx]) grid[rowIdx] = [];
      let colIdx = 0;
      Array.from(tr.cells).forEach((cell) => {
        while (grid[rowIdx][colIdx]) colIdx++;
        const rowspan = cell.rowSpan || 1;
        const colspan = cell.colSpan || 1;
        for (let r = rowIdx; r < rowIdx + rowspan; r++) {
          if (!grid[r]) grid[r] = [];
          for (let c = colIdx; c < colIdx + colspan; c++) {
            grid[r][c] = {cell, startRow: rowIdx};
          }
        }
        colIdx += colspan;
      });
    });
    for (let rowIdx = grid.length - 1; rowIdx >= 0; rowIdx--) {
      const row = grid[rowIdx];
      if (!row) continue;
      const seen = new Set();
      row.forEach((entry) => {
        if (!entry || entry.startRow !== rowIdx || seen.has(entry.cell)) return;
        seen.add(entry.cell);
        const td = entry.cell;
        const bbs = td.style.borderBottomStyle;
        if (bbs !== 'solid' && bbs !== 'double') return;
        const cellZ = parseInt(td.style.zIndex) || 0;
        const cellColStart = row.indexOf(entry);
        const colspan = td.colSpan || 1;
        const nextRowIdx = rowIdx + (td.rowSpan || 1);
        const nextRow = grid[nextRowIdx];
        if (!nextRow) return;
        let maxBelowZ = -Infinity;
        const seenBelow = new Set();
        for (let c = cellColStart; c < cellColStart + colspan; c++) {
          const below = nextRow[c];
          if (!below || seenBelow.has(below.cell)) continue;
          seenBelow.add(below.cell);
          maxBelowZ = Math.max(
            maxBelowZ,
            parseInt(below.cell.style.zIndex) || 0
          );
        }
        if (maxBelowZ >= cellZ) {
          if (!td.style.position) td.style.position = 'relative';
          td.style.zIndex = String(maxBelowZ + 1);
        }
      });
    }
  });
}

// ─── Justification Preservation ──────────────────────────────────────────────

/**
 * When a justified paragraph is split, the top fragment's last line becomes
 * the last line of a "virtual paragraph" and the browser renders it
 * left-aligned (the default `text-align-last`).  This helper forces the
 * last line to stay justified so the PDF matches the editor.
 *
 * Safe to call on non-justified elements — it's a no-op if textAlign !== 'justify'.
 *
 * @param {Element} sourceEl  - the original (unsplit) element
 * @param {Element} topClone  - the top-half clone to fix
 */
export function preserveJustification(sourceEl, topClone) {
  if (!sourceEl || !topClone) return;
  const win = sourceEl?.ownerDocument?.defaultView || window;
  const srcAlign = (
    win.getComputedStyle(sourceEl).textAlign || ''
  ).toLowerCase();
  if (srcAlign === 'justify') {
    topClone.style.setProperty('text-align-last', 'justify');
    topClone.style.setProperty('-moz-text-align-last', 'justify');
    return;
  }

  // For container splits (e.g. UL/OL), only apply to descendant blocks whose
  // text was actually truncated in the top fragment. This avoids forcing
  // text-align-last on unchanged lead-in paragraphs that happen to be justified.
  const justifiedDescendants = Array.from(
    topClone.querySelectorAll?.('[style*="text-align: justify"]') || []
  );
  justifiedDescendants.forEach((descendantEl) => {
    const pathFromTopClone = getNodePath(topClone, descendantEl);
    if (!pathFromTopClone) return;
    const sourceDescendant = resolveNodePath(sourceEl, pathFromTopClone);
    if (!sourceDescendant || sourceDescendant.nodeType !== Node.ELEMENT_NODE)
      return;
    const sourceText = normalizeBoundaryText(
      sourceDescendant.textContent || ''
    );
    const topText = normalizeBoundaryText(descendantEl.textContent || '');
    if (!topText) return;
    // Only preserve "last-line justify" when this descendant was actually
    // shortened by the split in the top fragment.
    if (!sourceText || topText.length >= sourceText.length) return;
    descendantEl.style.setProperty('text-align-last', 'justify');
    descendantEl.style.setProperty('-moz-text-align-last', 'justify');
  });
}

/**
 * Collect non-zero scroll offsets keyed by child-index paths.
 *
 * Paths are relative to `rootEl` and allow restoring scroll offsets
 * onto structurally identical clones.
 *
 * @param {Element} rootEl
 * @returns {Array<{path: number[], scrollLeft: number, scrollTop: number}>}
 */
export function buildScrollPathState(rootEl) {
  if (!rootEl) return [];
  const states = [];
  const stack = [{node: rootEl, path: []}];
  while (stack.length) {
    const {node, path} = stack.pop();
    if (!node) continue;
    const left = node.scrollLeft || 0;
    const top = node.scrollTop || 0;
    if (left || top) states.push({path, scrollLeft: left, scrollTop: top});
    const children = node.children || [];
    for (let i = children.length - 1; i >= 0; i -= 1) {
      stack.push({node: children[i], path: [...path, i]});
    }
  }
  return states;
}

/**
 * Resolve a descendant element by a child-index path.
 *
 * @param {Element} rootEl
 * @param {number[]} path
 * @returns {Element | null}
 */
export function resolveNodeAtPath(rootEl, path) {
  if (!rootEl) return null;
  let node = rootEl;
  for (const idx of path || []) {
    node = node?.children?.[idx] || null;
    if (!node) return null;
  }
  return node;
}

/**
 * Apply saved scroll-path state to a root element subtree.
 *
 * When native `scrollLeft` does not stick (common on print-time absolutely
 * positioned clones), this emulates horizontal scroll by translating direct
 * children and clipping overflow.
 *
 * @param {Element} rootEl
 * @param {Array<{path: number[], scrollLeft: number, scrollTop: number}>} scrollStates
 * @returns {{applied: number, requested: number, emulatedHorizontal: number}}
 */
export function applyScrollPathState(rootEl, scrollStates) {
  if (!rootEl || !Array.isArray(scrollStates) || !scrollStates.length) {
    return {
      applied: 0,
      requested: Array.isArray(scrollStates) ? scrollStates.length : 0,
      emulatedHorizontal: 0,
    };
  }
  let applied = 0;
  let emulatedHorizontal = 0;
  scrollStates.forEach((state) => {
    const node = resolveNodeAtPath(rootEl, state.path);
    if (!node) return;
    const desiredLeft = state.scrollLeft || 0;
    node.scrollLeft = desiredLeft;
    node.scrollTop = state.scrollTop || 0;
    if (desiredLeft > 0 && (node.scrollLeft || 0) !== desiredLeft) {
      // Detached/absolutely-positioned print clones can ignore native
      // horizontal scroll state. Emulate viewport by translating children.
      node.style.overflowX = 'hidden';
      node.style.position = node.style.position || 'relative';
      Array.from(node.children || []).forEach((child) => {
        const existingTransform = (child.style.transform || '').trim();
        // Preserve existing transform chain and avoid stacking duplicate offsets.
        if (existingTransform.includes('translateX(')) return;
        child.style.transform = `translateX(-${desiredLeft}px)${
          existingTransform ? ` ${existingTransform}` : ''
        }`;
      });
      emulatedHorizontal += 1;
    }
    applied += 1;
  });
  return {applied, requested: scrollStates.length, emulatedHorizontal};
}

/**
 * Clone an element and attach path-based scroll metadata for later replay.
 *
 * @param {Element} el
 * @returns {Element}
 */
export function cloneWithScrollPaths(el) {
  if (!el?.cloneNode) return el;
  const sourcePathStates = buildScrollPathState(el);
  const clone = el.cloneNode(true);
  // Attach metadata to each fragment clone so render-time replay can restore
  // per-subtree scroll state after split/clone operations.
  clone.__printScrollPathStates = sourcePathStates;
  return clone;
}

// ─── Text Line Visibility ────────────────────────────────────────────────────

/**
 * Classify whether an element has fully-visible text lines ABOVE and/or
 * BELOW a horizontal cut at `cutHeight` pixels from the element's top.
 *
 * Use this variant only in print decoupage paths. It measures against an
 * offscreen iframe clone to match print-time layout. For live editor geometry
 * (pagination + W/O), use `classifyLineVisibilityAtCut` from
 * `pagination/lineGeometry.utils.js`.
 *
 * This is the core decision function for boundary handling:
 *   - above=true, below=false  → all text fits on current page ("current_only")
 *   - above=false, below=true  → all text is past the cut ("next_only")
 *   - above=true, below=true   → text straddles the cut ("split")
 *
 * Falls back to geometry-based classification when no renderable text lines
 * are found (e.g., whitespace-only paragraphs, nbsp runs).
 *
 * @param {Element}  el             - the element to classify
 * @param {number}   cutHeight      - page-local Y offset of the boundary
 * @param {number}   width          - CSS-pixel width for measurement
 * @param {Document} iframeDoc      - the iframe's document
 * @param {string}   tiptapClassName
 * @param {string}   tiptapCSS
 * @returns {{ above: boolean, below: boolean }}
 */
export function classifyLineVisibilityAtCutInIframe(
  el,
  cutHeight,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  if (!el)
    return {
      above: false,
      below: false,
      aboveLineCount: 0,
      belowLineCount: 0,
      totalLineCount: 0,
    };
  if (cutHeight <= 0)
    return {
      above: false,
      below: true,
      aboveLineCount: 0,
      belowLineCount: 0,
      totalLineCount: 0,
    };

  const {mClone, cleanup} = createMeasurementClone(
    el,
    width,
    iframeDoc,
    tiptapClassName,
    tiptapCSS
  );
  if (!mClone) {
    cleanup();
    return {
      above: false,
      below: false,
      aboveLineCount: 0,
      belowLineCount: 0,
      totalLineCount: 0,
    };
  }

  const baseRect = mClone.getBoundingClientRect();
  const textNodes = collectTextNodes(mClone);
  let above = false;
  let below = false;
  let sawRenderableLine = false;
  const lineBottomBuckets = new Map();

  for (const tn of textNodes) {
    const txt = tn.textContent || '';
    if (!txt.trim()) continue;
    const range = iframeDoc.createRange();
    range.selectNodeContents(tn);
    const rects = range.getClientRects();
    for (const r of rects) {
      if (r.width <= 0 || r.height <= 0) continue;
      sawRenderableLine = true;
      const relTop = r.top - baseRect.top;
      const relBottom = r.bottom - baseRect.top;
      // Group line bottoms into stable buckets to avoid duplicate counting
      // from adjacent text nodes on the same rendered line.
      const bottomKey = Math.round(relBottom * 2) / 2;
      if (!lineBottomBuckets.has(bottomKey)) {
        lineBottomBuckets.set(bottomKey, relBottom);
      }
      if (relTop >= 0 && relBottom <= cutHeight) above = true;
      if (relBottom > cutHeight) below = true;
      if (above && below) break;
    }
    if (above && below) break;
  }

  if (!sawRenderableLine) {
    above = cutHeight > 0;
    below = baseRect.height > cutHeight;
  }

  const lineBottoms = Array.from(lineBottomBuckets.values()).sort(
    (a, b) => a - b
  );
  const aboveLineCount = lineBottoms.filter(
    (bottom) => bottom <= cutHeight
  ).length;
  const totalLineCount = lineBottoms.length;
  const belowLineCount = Math.max(0, totalLineCount - aboveLineCount);

  cleanup();
  return {above, below, aboveLineCount, belowLineCount, totalLineCount};
}

// ─── Content Splitting ───────────────────────────────────────────────────────
// The splitting subsystem produces {topClone, bottomClone} pairs from a single
// DOM element that straddles a page boundary. Every function returns null when
// a valid split cannot be produced (caller should fall back to clip or carry).
//
// Dispatch hierarchy (splitNodeAtHeight):
//   UL/OL → splitListAtHeight → splitTextContentAtHeight
//   LI    → splitContainerChildrenAtHeight → splitTextContentAtHeight
//   P/H*  → splitTextContentAtHeight → splitContainerChildrenAtHeight
//   TABLE → null (always clipped)
//   other → splitContainerChildrenAtHeight → splitTextContentAtHeight
//
// splitAtContractAnchor bypasses this hierarchy entirely: it uses the
// editor-provided {path, offset} to cut deterministically at the same
// text position the editor chose, avoiding any measurement.

/**
 * Split a text-bearing element into two clones at the line that crosses
 * `cutHeight` pixels from the element's top.
 *
 * Works for any element whose content is ultimately text nodes (p, h1-h6,
 * li, div, blockquote, ul, ol).  For TABLE elements the caller should use
 * clip-based handling instead (splitting mid-cell breaks table layout).
 *
 * Algorithm:
 *   1. Mount an offscreen measurement clone at the target width
 *   2. Binary-search each text node to find the last visible character
 *   3. Build top/bottom clones by pruning the DOM tree at the split point
 *   4. Validate the split with `hasRiskySplitOverlap`
 *   5. Preserve justification on the top fragment
 *
 * @returns {{ topClone: Element, bottomClone: Element } | null}
 */
export function splitTextContentAtHeight(
  el,
  cutHeight,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  if (!el || cutHeight <= 0) return null;
  const {mClone, cleanup} = createMeasurementClone(
    el,
    width,
    iframeDoc,
    tiptapClassName,
    tiptapCSS
  );
  if (!mClone) {
    cleanup();
    return null;
  }

  const textNodes = collectTextNodes(mClone);
  if (textNodes.length === 0) {
    cleanup();
    return null;
  }

  const mCloneRect = mClone.getBoundingClientRect();
  let breakInfo = null;
  let lastVisible = null;

  function maxVisibleCharsInTextNode(tn) {
    if (!tn || !tn.textContent || tn.textContent.length === 0) return 0;
    let lo = 0;
    let hi = tn.textContent.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      const r = iframeDoc.createRange();
      r.setStart(tn, 0);
      r.setEnd(tn, mid);
      const br = r.getBoundingClientRect();
      const relBottom = br.bottom - mCloneRect.top;
      if (relBottom <= cutHeight) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  for (const tn of textNodes) {
    const path = getNodePath(mClone, tn);
    if (!path) continue;

    const visibleChars = maxVisibleCharsInTextNode(tn);
    if (visibleChars > 0) {
      lastVisible = {path, idx: visibleChars};
    }

    if (visibleChars < tn.textContent.length) {
      if (!lastVisible) break;
      breakInfo = {
        topPath: lastVisible.path,
        topCharIdx: lastVisible.idx,
        bottomPath: path,
        bottomCharIdx: visibleChars,
      };
      break;
    }
  }

  cleanup();
  if (!breakInfo) return null;

  const topClone = el.cloneNode(true);
  const bottomClone = el.cloneNode(true);

  const topTN = resolveNodePath(topClone, breakInfo.topPath);
  const bottomTN = resolveNodePath(bottomClone, breakInfo.bottomPath);
  if (!topTN || !bottomTN) return null;

  topTN.textContent = topTN.textContent.substring(0, breakInfo.topCharIdx);
  pruneAfter(topClone, topTN);

  bottomTN.textContent = bottomTN.textContent.substring(
    breakInfo.bottomCharIdx
  );
  pruneBefore(bottomClone, bottomTN);

  cleanEmptyInlines(topClone);
  cleanEmptyInlines(bottomClone);

  const originalText = normalizeBoundaryText(el.textContent || '');
  const topText = normalizeBoundaryText(topClone.textContent || '');
  const bottomText = normalizeBoundaryText(bottomClone.textContent || '');
  if (hasRiskySplitOverlap(originalText, topText, bottomText)) return null;

  preserveJustification(el, topClone);

  return {topClone, bottomClone};
}

/**
 * Split an element at a pre-computed text anchor from the editor's
 * pagination break contract.
 *
 * The `splitAnchor` specifies { path: number[], offset: number } pointing
 * at the exact text node and character offset where the editor decided
 * the page boundary falls.  This makes the PDF split deterministically
 * match the editor.
 *
 * @param {Element} el          - the element to split
 * @param {{ path: number[], offset: number }} splitAnchor
 * @returns {{ topClone: Element, bottomClone: Element } | null}
 */
export function splitAtContractAnchor(el, splitAnchor) {
  if (!el || !splitAnchor || !Array.isArray(splitAnchor.path)) return null;
  const offset = Number(splitAnchor.offset);
  if (!Number.isFinite(offset) || offset <= 0) return null;

  // Fallback #1: nested-list boundary split.
  // Why this exists: contract anchors may point into nested list text where
  // direct text-node split would duplicate ancestor lead-in copy on the bottom
  // page. Splitting at the nested list boundary preserves semantic continuation.
  const splitNestedListAtAnchorBoundary = () => {
    const tagName = (el.tagName || '').toLowerCase();
    if (tagName !== 'ul' && tagName !== 'ol') return null;
    const anchorNode = resolveNodePath(el, splitAnchor.path);
    if (!anchorNode) return null;
    const anchorElement =
      anchorNode.nodeType === Node.ELEMENT_NODE
        ? anchorNode
        : anchorNode.parentElement;
    const anchorLi = anchorElement?.closest?.('li');
    if (!anchorLi || !el.contains(anchorLi)) return null;

    const boundaryList = anchorLi.parentElement;
    const boundaryListTag = (boundaryList?.tagName || '').toLowerCase();
    if (
      !boundaryList ||
      (boundaryListTag !== 'ul' && boundaryListTag !== 'ol') ||
      !el.contains(boundaryList)
    ) {
      return null;
    }

    const listPathFromRoot = getNodePath(el, boundaryList);
    if (!listPathFromRoot) return null;
    const splitChildIndex = Array.from(boundaryList.children || []).indexOf(
      anchorLi
    );
    if (
      splitChildIndex < 0 ||
      splitChildIndex >= Array.from(boundaryList.children || []).length - 1
    ) {
      return null;
    }

    const topClone = el.cloneNode(true);
    const bottomClone = el.cloneNode(true);
    const topBoundaryList = resolveNodePath(topClone, listPathFromRoot);
    const bottomBoundaryList = resolveNodePath(bottomClone, listPathFromRoot);
    if (!topBoundaryList || !bottomBoundaryList) return null;

    const topChildren = Array.from(topBoundaryList.children || []);
    const bottomChildren = Array.from(bottomBoundaryList.children || []);
    for (let i = topChildren.length - 1; i > splitChildIndex; i--) {
      topBoundaryList.removeChild(topChildren[i]);
    }
    for (let i = 0; i <= splitChildIndex && i < bottomChildren.length; i++) {
      bottomBoundaryList.removeChild(bottomChildren[i]);
    }

    // In nested-list splits, the bottom fragment should continue from the
    // boundary list level and must not duplicate ancestor introductory text.
    // Remove siblings before the boundary list inside its parent LI.
    const bottomBoundaryParent = bottomBoundaryList.parentElement;
    if (
      bottomBoundaryParent &&
      bottomBoundaryParent.tagName &&
      bottomBoundaryParent.tagName.toLowerCase() === 'li'
    ) {
      while (
        bottomBoundaryParent.firstChild &&
        bottomBoundaryParent.firstChild !== bottomBoundaryList
      ) {
        bottomBoundaryParent.removeChild(bottomBoundaryParent.firstChild);
      }
    }

    if (boundaryListTag === 'ol') {
      const startVal = parseInt(boundaryList.getAttribute('start'), 10) || 1;
      const topItems = Array.from(topBoundaryList.children || []).filter(
        (c) => c.tagName && c.tagName.toLowerCase() === 'li'
      ).length;
      bottomBoundaryList.setAttribute('start', String(startVal + topItems));
    }

    const topText = (topClone.textContent || '').trim();
    const bottomText = (bottomClone.textContent || '').trim();
    if (!topText || !bottomText) return null;
    preserveJustification(el, topClone);
    return {topClone, bottomClone};
  };

  // Fallback #2: direct list boundary split.
  // Used when anchor resolves inside a list item but not a splittable text node.
  const splitListAtAnchorBoundary = () => {
    const tagName = (el.tagName || '').toLowerCase();
    if (tagName !== 'ul' && tagName !== 'ol') return null;
    const anchorNode = resolveNodePath(el, splitAnchor.path);
    if (!anchorNode) return null;

    const directChildren = Array.from(el.children || []).filter(
      (child) => child.tagName && child.tagName.toLowerCase() === 'li'
    );
    if (directChildren.length <= 1) return null;

    const anchorLiIdx = directChildren.findIndex((child) =>
      child.contains(anchorNode)
    );
    if (anchorLiIdx < 0 || anchorLiIdx >= directChildren.length - 1)
      return null;

    const topClone = el.cloneNode(true);
    const bottomClone = el.cloneNode(true);
    const topChildren = Array.from(topClone.children || []);
    const bottomChildren = Array.from(bottomClone.children || []);

    for (let i = topChildren.length - 1; i > anchorLiIdx; i--) {
      topClone.removeChild(topChildren[i]);
    }
    for (let i = 0; i <= anchorLiIdx && i < bottomChildren.length; i++) {
      bottomClone.removeChild(bottomChildren[i]);
    }

    if (!topClone.children.length || !bottomClone.children.length) return null;

    if (tagName === 'ol') {
      const startVal = parseInt(el.getAttribute('start'), 10) || 1;
      const topItems = Array.from(topClone.children || []).filter(
        (c) => c.tagName && c.tagName.toLowerCase() === 'li'
      ).length;
      bottomClone.setAttribute('start', String(startVal + topItems));
    }

    preserveJustification(el, topClone);
    return {topClone, bottomClone};
  };

  const topClone = el.cloneNode(true);
  const bottomClone = el.cloneNode(true);
  const topTN = resolveNodePath(topClone, splitAnchor.path);
  const bottomTN = resolveNodePath(bottomClone, splitAnchor.path);
  if (
    !topTN ||
    !bottomTN ||
    topTN.nodeType !== Node.TEXT_NODE ||
    bottomTN.nodeType !== Node.TEXT_NODE
  ) {
    // Non-text anchor targets (or unresolved paths) can still be valid list
    // boundaries, so try list-aware fallbacks before giving up.
    return splitListAtAnchorBoundary() || splitNestedListAtAnchorBoundary();
  }

  const topLen = topTN.textContent?.length || 0;
  const cut = Math.max(0, Math.min(topLen, offset));
  if (cut <= 0 || cut >= topLen) {
    // Degenerate character splits are treated as boundary splits for lists.
    return splitListAtAnchorBoundary() || splitNestedListAtAnchorBoundary();
  }

  topTN.textContent = topTN.textContent.substring(0, cut);
  pruneAfter(topClone, topTN);
  bottomTN.textContent = bottomTN.textContent.substring(cut);
  pruneBefore(bottomClone, bottomTN);
  cleanEmptyInlines(topClone);
  cleanEmptyInlines(bottomClone);

  const topText = (topClone.textContent || '').trim();
  const bottomText = (bottomClone.textContent || '').trim();
  if (!topText || !bottomText) return null;

  // Preserve ordered-list continuity when splitting at contract anchors.
  // For clean OL boundaries, bottom starts after all items rendered in top.
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'ol') {
    const startVal = parseInt(el.getAttribute('start'), 10) || 1;
    const topItems = Array.from(topClone.children || []).filter(
      (c) => c.tagName && c.tagName.toLowerCase() === 'li'
    ).length;
    bottomClone.setAttribute('start', String(startVal + topItems));
  }

  preserveJustification(el, topClone);
  return {topClone, bottomClone};
}

/**
 * Split a container element at child-element boundaries.
 *
 * Walks the direct children of `el`, finds the one that straddles
 * `cutHeight`, and recursively calls `splitNodeAtHeight` on it.
 * Children fully above the cut go to topClone, fully below to bottomClone.
 *
 * Falls back to `splitTextContentAtHeight` if the element has no children
 * (i.e., it's a leaf with only text nodes).
 *
 * @returns {{ topClone: Element, bottomClone: Element } | null}
 */
export function splitContainerChildrenAtHeight(
  el,
  cutHeight,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  if (!el || cutHeight <= 0) return null;

  const {mClone, cleanup} = createMeasurementClone(
    el,
    width,
    iframeDoc,
    tiptapClassName,
    tiptapCSS
  );
  if (!mClone) {
    cleanup();
    return null;
  }

  const mCloneRect = mClone.getBoundingClientRect();
  const mChildren = Array.from(mClone.children);
  if (mChildren.length === 0) {
    cleanup();
    return splitTextContentAtHeight(
      el,
      cutHeight,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );
  }

  let splitIdx = -1;
  let childRelTop = 0;
  let childWidth = width;
  let childStraddles = false;

  for (let i = 0; i < mChildren.length; i++) {
    const r = mChildren[i].getBoundingClientRect();
    const relTop = r.top - mCloneRect.top;
    const relBot = r.bottom - mCloneRect.top;

    if (relTop >= cutHeight) {
      splitIdx = i;
      childStraddles = false;
      break;
    }
    if (relBot > cutHeight) {
      splitIdx = i;
      childRelTop = relTop;
      childWidth = r.width || width;
      childStraddles = true;
      break;
    }
  }

  cleanup();

  if (splitIdx < 0) return null;
  if (splitIdx === 0 && !childStraddles) return null;

  const origChildren = Array.from(el.children);
  const topClone = el.cloneNode(true);
  const bottomClone = el.cloneNode(true);
  const topChildren = Array.from(topClone.children);
  const bottomChildren = Array.from(bottomClone.children);

  if (childStraddles && splitIdx < origChildren.length) {
    const childCutH = cutHeight - childRelTop;
    const childSplit = splitNodeAtHeight(
      origChildren[splitIdx],
      childCutH,
      childWidth,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );

    if (childSplit) {
      for (let i = topChildren.length - 1; i > splitIdx; i--)
        topClone.removeChild(topChildren[i]);
      topClone.replaceChild(childSplit.topClone, topChildren[splitIdx]);

      for (let i = 0; i < splitIdx; i++)
        bottomClone.removeChild(bottomChildren[i]);
      bottomClone.replaceChild(
        childSplit.bottomClone,
        bottomChildren[splitIdx]
      );
    } else {
      for (let i = topChildren.length - 1; i >= splitIdx; i--)
        topClone.removeChild(topChildren[i]);
      for (let i = 0; i < splitIdx; i++)
        bottomClone.removeChild(bottomChildren[i]);
    }
  } else {
    for (let i = topChildren.length - 1; i >= splitIdx; i--)
      topClone.removeChild(topChildren[i]);
    for (let i = 0; i < splitIdx; i++)
      bottomClone.removeChild(bottomChildren[i]);
  }

  cleanEmptyInlines(topClone);
  cleanEmptyInlines(bottomClone);

  const topHasContent =
    topClone.children.length > 0 || (topClone.textContent || '').trim() !== '';
  const bottomHasContent =
    bottomClone.children.length > 0 ||
    (bottomClone.textContent || '').trim() !== '';
  if (!topHasContent || !bottomHasContent) return null;

  return {topClone, bottomClone};
}

/**
 * Split a UL or OL at LI boundaries.
 *
 * If the boundary falls between two LIs, split cleanly.  If it falls
 * INSIDE an LI (straddling), recursively split the LI's content at the
 * line boundary and suppress the continuation marker on the bottom clone's
 * first LI.
 *
 * For straddling LIs, checks text-line visibility first: if no text line
 * is fully visible above the cut, the entire LI belongs to the next page
 * (avoids ghost fragments + marker suppression errors).
 *
 * For OL elements, adjusts the `start` attribute on the bottom clone so
 * numbering continues correctly.
 *
 * @returns {{ topClone: Element, bottomClone: Element } | null}
 */
export function splitListAtHeight(
  el,
  cutHeight,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  if (!el) return null;
  const LIST_SPLIT_EPSILON_PX = 1;
  const tag = el.tagName?.toLowerCase();
  if (tag !== 'ul' && tag !== 'ol') return null;

  const getDirectLis = (listEl) =>
    Array.from(listEl.children).filter(
      (c) => c.tagName && c.tagName.toLowerCase() === 'li'
    );

  const {mClone, cleanup} = createMeasurementClone(
    el,
    width,
    iframeDoc,
    tiptapClassName,
    tiptapCSS
  );
  if (!mClone) {
    cleanup();
    return null;
  }

  const mCloneRect = mClone.getBoundingClientRect();
  const liEls = getDirectLis(mClone);

  if (liEls.length === 0) {
    cleanup();
    return null;
  }

  let splitIdx = -1;
  let liRelTop = 0;
  let liRelBottom = 0;
  let liWidth = 0;
  let liStraddles = false;

  for (let i = 0; i < liEls.length; i++) {
    const r = liEls[i].getBoundingClientRect();
    const relTop = r.top - mCloneRect.top;
    const relBot = r.bottom - mCloneRect.top;

    // If an LI geometrically crosses the cut, treat it as straddling.
    // This prevents boundary-overlap cases (including sub-pixel drift) from
    // being misclassified as a clean split before the next LI.
    if (relTop < cutHeight && relBot > cutHeight) {
      splitIdx = i;
      liRelTop = relTop;
      liRelBottom = relBot;
      liWidth = r.width;
      liStraddles = true;
      break;
    }
    // Browser-zoom rounding can place boundary-adjacent LI tops slightly
    // before the intended split line. Keep an epsilon only for clean
    // boundary classification (between two LIs).
    if (relTop >= cutHeight + LIST_SPLIT_EPSILON_PX) {
      splitIdx = i;
      liStraddles = false;
      break;
    }
  }

  cleanup();

  if (splitIdx < 0) return null;
  if (splitIdx === 0 && !liStraddles) return null;

  const origLIs = getDirectLis(el);

  // Attempt to split the straddling LI's content at the line boundary.
  let liSplit = null;
  if (liStraddles && splitIdx < origLIs.length) {
    const liCutH = cutHeight - liRelTop;
    const liVisibility = classifyLineVisibilityAtCutInIframe(
      origLIs[splitIdx],
      liCutH,
      liWidth || width,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );

    if (!liVisibility.above && liVisibility.below) {
      // No visible text above cut — entire LI belongs to next page.
      liSplit = null;
    } else {
      // Try progressively broader strategies to split the LI content.
      liSplit =
        splitContainerChildrenAtHeight(
          origLIs[splitIdx],
          liCutH,
          liWidth,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        ) ||
        splitTextContentAtHeight(
          origLIs[splitIdx],
          liCutH,
          liWidth,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        ) ||
        splitContainerChildrenAtHeight(
          origLIs[splitIdx],
          liCutH,
          width,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        ) ||
        splitTextContentAtHeight(
          origLIs[splitIdx],
          liCutH,
          width,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        );
    }

    if (liSplit) {
      const topText = (liSplit.topClone?.textContent || '').trim();
      const bottomText = (liSplit.bottomClone?.textContent || '').trim();
      if (!topText || !bottomText) liSplit = null;
    }

    // Boundary-adjacent LI straddles can miss the trailing line by fractions
    // of a pixel and fail all split strategies. If content is visible above
    // but not yet detectable below, retry with a slightly higher cut.
    if (
      !liSplit &&
      liVisibility.above &&
      !liVisibility.below &&
      liRelBottom > cutHeight &&
      liRelBottom - cutHeight <= LIST_SPLIT_EPSILON_PX + 0.01
    ) {
      const biasedLiCutH = Math.max(0, liCutH - LIST_SPLIT_EPSILON_PX);
      const retrySplit =
        splitContainerChildrenAtHeight(
          origLIs[splitIdx],
          biasedLiCutH,
          liWidth,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        ) ||
        splitTextContentAtHeight(
          origLIs[splitIdx],
          biasedLiCutH,
          liWidth,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        ) ||
        splitContainerChildrenAtHeight(
          origLIs[splitIdx],
          biasedLiCutH,
          width,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        ) ||
        splitTextContentAtHeight(
          origLIs[splitIdx],
          biasedLiCutH,
          width,
          iframeDoc,
          tiptapClassName,
          tiptapCSS
        );
      if (retrySplit) {
        const topText = (retrySplit.topClone?.textContent || '').trim();
        const bottomText = (retrySplit.bottomClone?.textContent || '').trim();
        if (topText && bottomText) {
          liSplit = retrySplit;
        }
      }
    }
  }

  // Build the top/bottom UL/OL clones, partitioning LIs around splitIdx.
  const topClone = el.cloneNode(true);
  const bottomClone = el.cloneNode(true);
  const topLIs = getDirectLis(topClone);
  const bottomLIs = getDirectLis(bottomClone);

  if (liSplit) {
    // Straddling LI was successfully split — replace it in both clones.
    for (let i = topLIs.length - 1; i > splitIdx; i--)
      topClone.removeChild(topLIs[i]);
    topClone.replaceChild(liSplit.topClone, topLIs[splitIdx]);

    for (let i = 0; i < splitIdx; i++) bottomClone.removeChild(bottomLIs[i]);
    liSplit.bottomClone.style.listStyleType = 'none';
    bottomClone.replaceChild(liSplit.bottomClone, bottomLIs[splitIdx]);
  } else if (liStraddles) {
    // Straddle but couldn't split LI — push entire straddling LI to next page.
    for (let i = topLIs.length - 1; i >= splitIdx; i--)
      topClone.removeChild(topLIs[i]);
    for (let i = 0; i < splitIdx; i++) bottomClone.removeChild(bottomLIs[i]);
  } else {
    // Clean boundary between two LIs.
    for (let i = topLIs.length - 1; i >= splitIdx; i--)
      topClone.removeChild(topLIs[i]);
    for (let i = 0; i < splitIdx; i++) bottomClone.removeChild(bottomLIs[i]);
  }

  if (topClone.children.length === 0 || bottomClone.children.length === 0) {
    return null;
  }

  // Fix OL numbering on the bottom clone.
  if (tag === 'ol') {
    const startVal = parseInt(el.getAttribute('start'), 10) || 1;
    const botFirstLI = getDirectLis(bottomClone)[0] || null;
    const hasHiddenContinuation =
      botFirstLI && botFirstLI.style.listStyleType === 'none';
    const itemsInTop = getDirectLis(topClone).length;
    bottomClone.setAttribute(
      'start',
      String(startVal + itemsInTop + (hasHiddenContinuation ? -1 : 0))
    );
  }

  return {topClone, bottomClone};
}

/**
 * Run split strategies in order and return the first successful result.
 *
 * @param {Array<Function>} splitters - splitter fns with common signature
 * @param {Element} el
 * @param {number} cutHeight
 * @param {number} width
 * @param {Document} iframeDoc
 * @param {string} tiptapClassName
 * @param {string} tiptapCSS
 * @returns {{ topClone: Element, bottomClone: Element } | null}
 */
function runSplitStrategies(
  splitters,
  el,
  cutHeight,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  for (const splitter of splitters) {
    const split = splitter(
      el,
      cutHeight,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );
    if (split) return split;
  }
  return null;
}

/**
 * Universal entry point for splitting a straddling node at `cutHeight`.
 *
 * Dispatches to the appropriate strategy based on element type:
 *   - UL/OL → `splitListAtHeight`, then `splitTextContentAtHeight` fallback
 *   - LI    → `splitContainerChildrenAtHeight`, then text split; suppresses
 *             continuation marker on bottom clone
 *   - P, H1-H6 → `splitTextContentAtHeight`, then container children fallback
 *   - TABLE → null (tables are handled by clip-based overflow)
 *   - Other → `splitContainerChildrenAtHeight`, then text split fallback
 *
 * @returns {{ topClone: Element, bottomClone: Element } | null}
 */
export function splitNodeAtHeight(
  el,
  cutHeight,
  width,
  iframeDoc,
  tiptapClassName,
  tiptapCSS
) {
  if (!el) return null;
  const tag = el.tagName?.toLowerCase();

  if (tag === 'ul' || tag === 'ol') {
    return runSplitStrategies(
      [splitListAtHeight, splitTextContentAtHeight],
      el,
      cutHeight,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );
  }

  if (tag === 'li') {
    const liSplit = runSplitStrategies(
      [splitContainerChildrenAtHeight, splitTextContentAtHeight],
      el,
      cutHeight,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );
    if (liSplit?.bottomClone) {
      liSplit.bottomClone.style.listStyleType = 'none';
    }
    return liSplit;
  }

  if (tag === 'p' || /^h[1-6]$/.test(tag || '')) {
    return runSplitStrategies(
      [splitTextContentAtHeight, splitContainerChildrenAtHeight],
      el,
      cutHeight,
      width,
      iframeDoc,
      tiptapClassName,
      tiptapCSS
    );
  }

  if (tag === 'table') {
    return null;
  }

  return runSplitStrategies(
    [splitContainerChildrenAtHeight, splitTextContentAtHeight],
    el,
    cutHeight,
    width,
    iframeDoc,
    tiptapClassName,
    tiptapCSS
  );
}

// ─── Post-Split Normalization ────────────────────────────────────────────────

/**
 * After splitting a UL/OL, detect whether the first LI of the bottom clone
 * is a continuation of a straddling LI (same LI appears in both halves)
 * and suppress its bullet/number marker.
 *
 * Uses `data-id` matching: if the last LI of topClone and the first LI of
 * bottomClone share the same `data-id`, the split cut through one LI.
 *
 * This is safe for clean-boundary splits (where a UL splits between two LIs)
 * because the last top LI and first bottom LI will have DIFFERENT `data-id`s,
 * so no marker is suppressed.
 *
 * Must be called at every planner-level split site to cover all split paths
 * (contract anchor, inference, re-split of carried content).
 *
 * @param {Element} topClone    - the top half of the split
 * @param {Element} bottomClone - the bottom half of the split
 */
export function suppressContinuationMarkerAfterListSplit(
  topClone,
  bottomClone
) {
  if (!topClone || !bottomClone) return;
  const tag = (topClone.tagName || '').toLowerCase();
  if (tag !== 'ul' && tag !== 'ol') return;

  const isLi = (c) => (c.tagName || '').toLowerCase() === 'li';
  const topLIs = Array.from(topClone.children).filter(isLi);
  const botLIs = Array.from(bottomClone.children).filter(isLi);
  if (!topLIs.length || !botLIs.length) return;

  const lastTopId = topLIs[topLIs.length - 1].getAttribute('data-id');
  const firstBotId = botLIs[0].getAttribute('data-id');
  if (lastTopId && firstBotId && lastTopId === firstBotId) {
    // Split-time responsibility is marker suppression only.
    // Ordered-list start semantics are finalized later in the post-decoupage
    // pass (usePrintDocumentV2) where full cross-page context is available.
    botLIs[0].style.listStyleType = 'none';
  }
}
